import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'

import Modal from '../common/Modal'
import AccoladeSelect from './AccoladeSelect'
import CancelButton from '../common/buttons/CancelButton'
import SubmitButton from '../common/buttons/SubmitButton'
import { adminUpdateCoach } from '../../actions/coachActions'
import { getAllAccolades, getCoachAccolades } from '../../actions/accoladeActions'
import AvatarPlaceholder from '../../assets/images/mock/default-profile-icon.png'
import Loader from '../common/loader/legacy-loaders/Op36Loader'

import classes from './EditCoachAccoladesModal.module.scss'

const EditCoachAccoladesModal = ({ isOpen, onClose, coach }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [accoladesToAdd, setAccoladesToAdd] = useState([])
  const [accoladesToDelete, setAccoladesToDelete] = useState([])
  const [currentAccolades, setCurrentAccolades] = useState([])

  const allAccolades = useSelector(state => state.accolades.all_accolades || [])
  const coachAccolades = useSelector(state => state.accolades.accolades || [])

  const fullName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim()

  useEffect(() => {
    setIsLoading(true)
    Promise.all([dispatch(getAllAccolades()), dispatch(getCoachAccolades(coach.id))])
      .catch(() => toast.error(t('coaches.editAccolades.loadError'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setIsLoading(false))
  }, [coach, dispatch, t])

  useEffect(() => {
    if (coachAccolades && coachAccolades.length >= 0) {
      setCurrentAccolades([...coachAccolades])
    }
  }, [coachAccolades])

  const handleAddAccolade = (accoladeData) => {
    const newAccolade = { accolade_id: accoladeData.accolade_id, issued_date: accoladeData.issued_date }
    setCurrentAccolades([...currentAccolades, newAccolade])
    setAccoladesToAdd([...accoladesToAdd, { accolade_id: accoladeData.accolade_id, issued_date: accoladeData.issued_date }])
  }

  const handleDeleteAccolade = (accolade) => {
    setCurrentAccolades(currentAccolades.filter(acc => acc !== accolade))

    if (accolade.id) {
      setAccoladesToDelete([...accoladesToDelete, { id: accolade.id, _destroy: true }])
    } else {
      const accoladeIdToRemove = accolade.accolade?.id || accolade.accolade_id
      setAccoladesToAdd(accoladesToAdd.filter(acc => acc.accolade_id !== accoladeIdToRemove))
    }
  }

  const handleSubmit = () => {
    setIsSaving(true)

    const updateData = {
      id: coach.id,
      user_accolades_attributes: [...accoladesToAdd, ...accoladesToDelete],
    }

    dispatch(adminUpdateCoach(updateData))
      .then(() => {
        toast.success(t('coaches.editAccolades.updateSuccess'), { position: toast.POSITION.TOP_RIGHT })
        onClose()
      })
      .catch(() => toast.error(t('coaches.editAccolades.updateError'), { position: toast.POSITION.TOP_RIGHT }))
      .finally (() => setIsSaving(false))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={classes.editCoachAccoladesModalContent}
    >
      <div className={classes.editCoachAccoladesModal}>
        <div className='d-flex flex-column mb-5'>
          <div className={classes.titleSection}>
            <h2 className={classes.modalTitle}>{t('coaches.editAccolades.title')}</h2>
            <p className={classes.modalSubtitle}>
              {t('coaches.editAccolades.subtitle')}
            </p>
          </div>
        </div>

        <div className='pb-4'>
          <img
            src={coach.avatar?.file_url || AvatarPlaceholder}
            className={classes.coachAvatar}
            alt={t('coaches.editAccolades.avatarAlt', { name: fullName })}
          />
          <span className='font-weight-bold ml-2'>{fullName}</span>
        </div>

        {isLoading ? (
          <div className='pt-4 pb-5'>
            <Loader message={t('coaches.editAccolades.loadingAccolades')} fullScreen={false} />
          </div>
        ) : (
          <div className='d-flex flex-column'>
            <AccoladeSelect
              allAccolades={allAccolades}
              currentAccolades={currentAccolades}
              onAddAccolade={handleAddAccolade}
              onDeleteAccolade={handleDeleteAccolade}
            />
          </div>
        )}

        <div className='d-flex justify-content-center align-items-center pt-4'>
          <CancelButton onClick={onClose} buttonMessage={t('common.cancel')} />
          <SubmitButton
            onClick={handleSubmit}
            buttonMessage={isSaving ? t('coaches.editAccolades.updating') : t('common.action.update')}
            loading={isSaving}
            noTopMargin
          />
        </div>
      </div>
    </Modal>
  )
}

EditCoachAccoladesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  coach: PropTypes.object,
}

export default EditCoachAccoladesModal
